import React, { useEffect, useState } from 'react';
import { TableDefaultSticky } from '../../../../shared/components/table/TableDefaultSticky';
import { TableColumnType } from '../../../../shared/components/table/table-types';
import { useCallAction, useEvent, useStore } from '@cobuildlab/react-simple-state';
import { useAuth } from '../../../auth/auth-hook';
import { changeDashboardDateFilterStore } from '../../dashboard-events';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { ButtonDefault } from '../../../../shared/components/ButtonDefault';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { getDateFilterText, subscriptionStatus } from '../../dashboard-utils';
import { fetchSubscriptionsRecurrentEvent } from '../../../subscription/subscription-events';
import {
  fetchGenerateSubscriptionUserReport,
  fetchUserSubscriptionsRecurrent,
} from '../../../subscription/subscription-actions';
import { GlobalSubscriptionTable } from '../../../subscription/subscription-types';
import { SubscriptionTableFilter } from '../../../subscription/components/SubscriptionTableFilter';
import { subscriptionTableFilterStore } from '../../../subscription/subscription-store';
import { useStylesCustom } from '../DashboardCardItem';
import { useTableSort } from '../../../../shared/components/table/useTableSort';
import { SubscriptionUserModal } from './SubscriptionUserModal';
import {formatCurrencyMXN, getDateByTimezone} from '../../../../shared/utils';
import { openSnackbarAction } from '../../../../shared/components/snackbar/snackbar-actions';
import { TypeMessage } from '../../../../shared/components/snackbar/snackbar-types';
import { PRIMARY_COLOR } from '../../../../shared/css/theme';
import { FRONT_URL } from '../../../../shared/constants';

export const DashboardSubscriptionsRecurrentTable: React.FC = () => {
  const classes = useStylesCustom();
  const [openModal, setOpenModal] = useState(false);
  const [currentUserSubscription, setCurrentUserSubscription] = useState<GlobalSubscriptionTable>();
  const { data, total, paymentMethods } = useEvent(fetchSubscriptionsRecurrentEvent);
  const { from, to, filter } = useStore(changeDashboardDateFilterStore);
  const [callFetchUsers, loading] = useCallAction(fetchUserSubscriptionsRecurrent);
  const { status, paymentMethod } = useStore(subscriptionTableFilterStore);
  const { sort, handleSort } = useTableSort();

  const [callReport, loadingReport] = useCallAction(fetchGenerateSubscriptionUserReport, {
    onCompleted: ({ report }) => {
      if (!report || report?.length === 0 || typeof report === 'string') {
        openSnackbarAction('Su reporte estara disponible en unos minutos', TypeMessage.SUCCESS);
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(
        report.map((r) => {
          return {
            ID: r.id,
            'Fecha inicio periodo': getDateByTimezone(r.period_start_date || r.date),
            'Fecha fin periodo': getDateByTimezone(r.period_end_date),
            nombre: r.user ? r.user.name || '' : '',
            correo: r.user ? r.user.email || '' : '',
            plan: r.plan.title,
            'Frecuencia de Plan': r.plan.frequency === 'YEAR' ? 'Anual' : 'Mensual',
            monto: formatCurrencyMXN(r.plan.price),
            status: subscriptionStatus(r.status),
            subscription_id: r.transaction_id || '',
            user_id: r.user ? r.user.id || '' : '',
            'Fecha Cancelación': r?.cancel_date
              ? getDateByTimezone(r.cancel_date)
              : '',
            'Metodo de pago': r.payment_method === 'STRIPE_CARD' ? 'STRIPE' : r.payment_method,
            'Tipo de Pago': r.type === 'PAYMENT' ? 'Primer pago' : 'Pago recurrente',
            'Url de suscripción': r.subscription_url
              ? r.subscription_url
              : `${FRONT_URL}/planes`,
            ...(r.user?.subscription_billing?.address && {
              'Calle y número': r.user.subscription_billing.address,
            }),
            ...(r.user?.subscription_billing?.address1 && {
              Colonia: r.user.subscription_billing.address1,
            }),
            ...(r.user?.subscription_billing?.city && {
              Ciudad: r.user.subscription_billing.city,
            }),
            ...(r.user?.subscription_billing?.state && {
              Estado: r.user.subscription_billing.state,
            }),
            ...(r.user?.subscription_billing?.country && {
              Pais: r.user.subscription_billing.country,
            }),
            ...(r.user?.subscription_billing?.postal_code && {
              'Código postal': r.user.subscription_billing.postal_code,
            }),
            ...(r.user?.subscription_billing?.phone && {
              Teléfono: r.user.subscription_billing.phone,
            }),
            ...(r.user?.subscription_billing?.phone1 && {
              'Teléfono 2': r.user.subscription_billing.phone1,
            }),
          };
        }),
      );
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, `${getDateFilterText(from, to, filter)}`);
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array',
      });
      const blob = new Blob([excelBuffer], {
        type: 'application/octet-stream',
      });
      saveAs(blob, `Reporte ${getDateFilterText(from, to, filter)}.xlsx`);
    },
  });

  const { user } = useAuth();

  const [pagination, setPagination] = useState({
    pageSize: 10,
    page: 1,
  });

  useEffect(() => {
    callFetchUsers({
      from,
      to,
      page: pagination.page - 1,
      status,
      paymentMethod,
      orderBy: sort,
      paymentType: 'RECURRENT_PAYMENT',
    });
  }, [callFetchUsers, from, pagination.page, to, status, paymentMethod, sort]);

  const handleOpenModal = (item: GlobalSubscriptionTable): void => {
    setCurrentUserSubscription(item);
    setOpenModal(true);
  };

  const COLUMNS: TableColumnType<GlobalSubscriptionTable>[] = [
    {
      columnName: 'FECHA DE SUSCRIPCIÓN',
      columnValue: (item) => getDateByTimezone(item.created_at),

      sortableField: 'period_start_date',
    },
    {
      columnName: 'NOMBRE',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.user_name}</Box>,
      sortableField: 'user_name',
    },
    {
      columnName: 'EMAIL',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.user_email as string}</Box>,
      sortableField: 'user_email',
    },
    {
      columnName: 'PLAN',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.plan_title as string}</Box>,
      sortableField: 'plan_title',
    },
    {
      columnName: 'N° DE SUSCRIPCIÓN',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.transaction_id as string}</Box>,
      sortableField: 'transaction_id',
    },
    {
      columnName: 'TIPO DE PAGO',
      columnValue: null,

      columnComponent: (item) => {
        if (!item.type) return <></>;

        return (
          <Box textAlign="left">{item.type === 'PAYMENT' ? 'Primer pago' : 'Pago recurrente'}</Box>
        );
      },
      sortableField: 'type',
    },
    {
      columnName: 'MÉTODO DE PAGO',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{item.payment_method as string}</Box>,
      sortableField: 'payment_method',
    },
    {
      columnName: 'STATUS',
      columnValue: null,

      columnComponent: (item) => <Box textAlign="left">{subscriptionStatus(item.status)}</Box>,
      sortableField: 'status',
    },
  ];

  return (
    <>
      <SubscriptionUserModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        userSubscription={currentUserSubscription}
      />
      <Box
        marginY={2}
        sx={{
          display: { xs: 'block', md: 'flex' },
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          component={'div'}
          sx={{ marginBottom: { xs: '10px', md: '0px' } }}
          fontSize={20}
          fontWeight={700}
        >
          Suscripciones recurrentes
        </Typography>
        <Box>
          <ButtonDefault
            onClick={() =>
              callReport({
                user,
                from,
                to,
                paymentType: 'RECURRENT_PAYMENT',
              })
            }
            startIcon={
              !loadingReport ? <img src="/icon-clip.svg" alt="clip" width={14} height={14} /> : null
            }
            isLoading={loadingReport}
            style={{
              width: 195,
              height: 40,
              textTransform: 'inherit',
              fontWeight: 700,
              background: PRIMARY_COLOR ?? 'black',
            }}
          >
            Descargar reporte
          </ButtonDefault>
          <SubscriptionTableFilter paymentMethodFilters={paymentMethods || []} />
        </Box>
      </Box>
      <Card
        elevation={2}
        style={{
          border: '1px solid #BBBBBB',
          padding: '16px',
          borderRadius: '16px',
          boxShadow: '0px 0px 8px 0px #00000014',
        }}
      >
        <CardContent className={classes.content}>
          <TableDefaultSticky
            count={total}
            items={data}
            columns={COLUMNS}
            page={pagination.page}
            pageSize={pagination.pageSize}
            loading={loading}
            messageLoading={'Cargando suscripciones registradas...'}
            handleClickRow={(item) => handleOpenModal(item)}
            handleChangePage={(page) => {
              setPagination({
                ...pagination,
                page: page + 1,
              });
            }}
            handleSort={handleSort}
          />
        </CardContent>
      </Card>
    </>
  );
};
